import { computed, ref, watch } from "vue";
import { defineStore } from "pinia";
import { useQuery } from "@vue/apollo-composable";
import { useRouter } from "vue-router";
import { accountsQuery, businessesQuery } from "@/api/accountApi";
import { BusinessRole } from "@/api/generated/graphql";
import useAuthUser from "@/composables/useAuthUser";
import { routeNames } from "@/router/constants";
import { usePaymentsStore } from "@/stores/payments";
import { type BankingContext, BankingContextType, PERSONAL_BANKING_CONTEXT } from "@/types/bankingContext";

const STORAGE_KEY_PREFIX = "money.bankingContext.";

function storageKey(userId: string) {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readStoredContext(userId: string): BankingContext {
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    if (!raw) {
      return PERSONAL_BANKING_CONTEXT;
    }
    const parsed = JSON.parse(raw) as BankingContext;
    if (parsed?.type === BankingContextType.Personal) {
      return PERSONAL_BANKING_CONTEXT;
    }
    if (parsed?.type === BankingContextType.Business && typeof parsed.entityId === "string" && parsed.entityId.length > 0) {
      return { type: BankingContextType.Business, entityId: parsed.entityId };
    }
  } catch (err) {
    // Ignore corrupt session values and fall back to personal.
    console.warn("Failed to read banking context from sessionStorage", err);
  }
  return PERSONAL_BANKING_CONTEXT;
}

function persistContext(userId: string, next: BankingContext) {
  try {
    sessionStorage.setItem(storageKey(userId), JSON.stringify(next));
  } catch (err) {
    // sessionStorage may be unavailable; in-memory context still works for the session.
    console.warn("Failed to persist banking context to sessionStorage", err);
  }
}

function firstBusinessContext(businesses: { id: string }[]): BankingContext | null {
  const first = businesses[0];
  return first ? { type: BankingContextType.Business, entityId: first.id } : null;
}

export const useBankingContextStore = defineStore("banking-context", () => {
  const router = useRouter();
  const { authUser } = useAuthUser();
  const isUserLoggedIn = computed(() => !!authUser.value);

  const context = ref<BankingContext>(PERSONAL_BANKING_CONTEXT);
  const switchingTo = ref<BankingContext | null>(null);
  const isSwitchingContext = computed(() => switchingTo.value !== null);

  const queryOptions = computed(() => ({
    enabled: isUserLoggedIn.value,
    fetchPolicy: "cache-and-network" as const,
  }));

  const { result: businessesResult, loading: loadingBusinesses, error: businessesError } = useQuery(businessesQuery, null, queryOptions);

  // Checks whether the customer has any personal accounts (default context + hide Personal in the switcher).
  const {
    result: personalAccountsResult,
    loading: loadingPersonalAccounts,
    error: personalAccountsError,
  } = useQuery(accountsQuery, { includeExternal: true, includeBusinessAccounts: false }, queryOptions);

  const businesses = computed(() => businessesResult.value?.businesses ?? []);
  // Don't confuse an error loading personal accounts with not having any
  const loadedPersonalAccounts = computed(() => personalAccountsResult.value !== undefined);
  const hasPersonalAccounts = computed(() => !loadedPersonalAccounts.value || (personalAccountsResult.value?.accounts?.length ?? 0) > 0);

  const activeBusiness = computed(() => {
    const current = context.value;
    if (current.type !== BankingContextType.Business) {
      return null;
    }
    return businesses.value.find(business => business.id === current.entityId) ?? null;
  });

  const activeBusinessName = computed(() => activeBusiness.value?.name ?? null);

  // Authorised Users (and similar admin-only settings) are only for callers who are ADMIN on the
  // active business. myRole is the calling customer's role on that entity.
  const isActiveBusinessAdmin = computed(() => activeBusiness.value?.myRole === BusinessRole.Admin);

  function applyContext(next: BankingContext) {
    context.value = next;
    if (authUser.value) {
      persistContext(authUser.value.userId, next);
    }
  }

  watch(
    authUser,
    user => {
      if (!user) {
        context.value = PERSONAL_BANKING_CONTEXT;
        switchingTo.value = null;
        return;
      }
      context.value = readStoredContext(user.userId);
    },
    { immediate: true },
  );

  // Once both queries have returned: business-only customers get a business context.

  watch(
    [businessesResult, loadingBusinesses, personalAccountsResult, loadingPersonalAccounts],
    ([businessesData, businessesLoading, accountsData, accountsLoading]) => {
      if (businessesLoading || accountsLoading || !businessesData || !accountsData) {
        return;
      }

      const businessList = businessesData.businesses ?? [];
      const noPersonalAccounts = (accountsData.accounts?.length ?? 0) === 0;
      const current = context.value;

      if (current.type === BankingContextType.Business) {
        if (businessList.some(business => business.id === current.entityId)) {
          return;
        }
      }
      if (noPersonalAccounts) {
        const businessOnly = firstBusinessContext(businessList);
        if (businessOnly) applyContext(businessOnly);
      } else applyContext(PERSONAL_BANKING_CONTEXT);
    },
    { immediate: true },
  );

  // Applies a context switch from the selector: shows the switching overlay, resets payment form
  // state that may still refer to the previous context's accounts, and lands on financial overview
  // so context-bound screens (e.g. Authorised Users) are left behind.
  async function setBankingContext(next: BankingContext) {
    if (next.type === BankingContextType.Personal && !hasPersonalAccounts.value) {
      return;
    }

    const current = context.value;
    if (
      current.type === BankingContextType.Personal
        ? next.type === BankingContextType.Personal
        : next.type === BankingContextType.Business && current.entityId === next.entityId
    ) {
      return;
    }

    switchingTo.value = next;
    try {
      applyContext(next);

      // Lazy: payments store depends on useAccounts which reads this store — avoid setup cycles.
      usePaymentsStore().resetPaymentForm();

      // Force a loading spinner to be displayed for at least this long
      const minDisplay = new Promise<void>(resolve => {
        window.setTimeout(resolve, 800);
      });
      await Promise.all([router.push({ name: routeNames.FINANCIAL_OVERVIEW }), minDisplay]);
    } finally {
      switchingTo.value = null;
    }
  }

  return {
    context,
    businesses,
    loadingBusinesses,
    businessesError,
    loadingPersonalAccounts,
    personalAccountsError,
    hasPersonalAccounts,
    activeBusiness,
    activeBusinessName,
    isActiveBusinessAdmin,
    switchingTo,
    isSwitchingContext,
    setBankingContext,
  };
});
